import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { shouldEnableRealtime } from "@/lib/realtimeUtils";

export interface NavigationNotifications {
  dashboard: number;
  create: number;
  vault: number;
  funZone: number;
  profile: number;
  viralStories: number;
  hallOfFame: number;
  contacts: number;
  unreadComments: number;
  unreadMessages: number;
}

export const useNavigationNotifications = () => {
  const [notifications, setNotifications] = useState<NavigationNotifications>({
    dashboard: 0,
    create: 0,
    vault: 0,
    funZone: 0,
    profile: 0,
    viralStories: 0,
    hallOfFame: 0,
    contacts: 0,
    unreadComments: 0,
    unreadMessages: 0,
  });
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    try {
      // Use getSession (faster, cached) instead of getUser (network call)
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setLoading(false);
        return;
      }
      const user = session.user;

      // Fetch pending drop invites (invites where user hasn't responded)
      const { data: pendingInvites } = await supabase
        .from("drop_invites")
        .select("id")
        .eq("user_id", user.id)
        .eq("status", "pending");

      // Fetch unviewed drops (invites not yet viewed)
      const { data: unviewedDrops } = await supabase
        .from("drop_invites")
        .select("id")
        .eq("user_id", user.id)
        .is("viewed_at", null);

      // Fetch pending event invitations
      const { data: pendingEventInvites } = await supabase
        .from("invitations")
        .select("id")
        .eq("user_id", user.id)
        .eq("status", "pending");

      // Get user's last read timestamps
      const { data: readStatuses } = await supabase
        .from("user_read_status")
        .select("content_type, last_read_at")
        .eq("user_id", user.id)
        .is("content_id", null);

      const readStatusMap: Record<string, string> = {};
      readStatuses?.forEach(s => {
        readStatusMap[s.content_type] = s.last_read_at;
      });

      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const newHofEntries: { id: string }[] = [];

      // Fetch new viral stories since last read or 7 days
      const storiesLastRead = readStatusMap["viral_stories"];
      const storiesCutoff = storiesLastRead && new Date(storiesLastRead) > sevenDaysAgo 
        ? storiesLastRead 
        : sevenDaysAgo.toISOString();
      
      const { data: newStories } = await supabase
        .from("bail_stories_public")
        .select("id")
        .gte("created_at", storiesCutoff);

      const dashboardCount = (pendingInvites?.length || 0) + (pendingEventInvites?.length || 0);
      const vaultCount = unviewedDrops?.length || 0;
      const funZoneCount = Math.min((newHofEntries?.length || 0) + (newStories?.length || 0), 9);

      setNotifications({
        dashboard: dashboardCount,
        create: 0,
        vault: vaultCount,
        funZone: funZoneCount,
        profile: 0,
        viralStories: newStories?.length || 0,
        hallOfFame: newHofEntries?.length || 0,
        contacts: 0,
        unreadComments: 0,
        unreadMessages: 0,
      });
    } catch (error) {
      console.error("Error fetching navigation notifications:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();

    // Skip realtime in testing environments to prevent WebSocket errors
    if (!shouldEnableRealtime()) {
      // Refresh every 30 seconds as fallback in testing
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }

    // Set up real-time subscription for drop_invites
    const channel = supabase
      .channel("navigation-notifications")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "drop_invites",
        },
        () => {
          fetchNotifications();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "invitations",
        },
        () => {
          fetchNotifications();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "bail_story_activity",
        },
        () => {
          fetchNotifications();
        }
      )
      .subscribe();

    // Refresh every 30 seconds as a fallback
    const interval = setInterval(fetchNotifications, 30000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, [fetchNotifications]);

  return { notifications, loading, refetch: fetchNotifications };
};

export default useNavigationNotifications;
